/**
 * Deployment Verification Dashboard
 *
 * Overview of AEO FAQ deployment verification status.
 * Shows stats, recent verifications, and ability to trigger batch verification.
 *
 * Part of AEO Deployment Verification System
 *
 * Date: January 23, 2026
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  Globe,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  ExternalLink,
  Play,
  Search,
  CheckSquare,
  TrendingUp,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  FileCheck,
  ArrowUpDown,
  Bot,
  Users,
  Trophy,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { PublisherManifestSection, DeploymentVerificationConfig } from '@/components/deployment-verification';

// Types
interface VerificationStats {
  deployed: number;
  partial: number;
  modified: number;
  notFound: number;
  errors: number;
  jsRequired: number;
  reported: number;
  publisherConfirmed: number;
  totalVerified: number;
  pendingVerification: number;
  avgSimilarity: number;
  lastVerified: string | null;
}

interface RatePreset {
  requestsPerSecond: number;
  maxConcurrent: number;
  description: string;
}

interface RatePresets {
  gentle: RatePreset;
  normal: RatePreset;
  fast: RatePreset;
}

interface ProductWithAEO {
  aeoId: string;
  aeoStatus: string;
  aeoUpdatedAt: string;
  faqCount: number;
  productTitle: string;
  sku: string;
  productUrl: string;
  category: string;
  brand: string;
  deploymentStatus: string | null;
  similarity: number | null;
  lastVerified: string | null;
}

interface VerificationResult {
  aeoId: string;
  url: string;
  productTitle: string;
  faqStatus: string;
  similarity: number;
  error?: string;
}

interface VerificationItem {
  id: string;
  siteId: string;
  siteDomain: string;
  url: string;
  faqStatus: string;
  faqSimilarity: number;
  lastVerified: string;
  details: {
    ourCount?: number;
    liveCount?: number;
    matchedCount?: number;
    checkedAt?: string;
  };
}

interface VerificationHistoryResponse {
  success: boolean;
  data: {
    items: VerificationItem[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
  };
}

// Impact types
interface ImpactProduct {
  aeoId: string;
  productName: string;
  pageUrl: string;
  approvedAt: string;
  deploymentStatus: string | null;
  verifiedAt: string | null;
}

interface ImpactOrganicData {
  date: string;
  totalTraffic: number;
  organicTraffic: number;
  searchOrganic: number;
  aiOrganic: number;
  organicPct: number;
  searchPct: number;
  aiPct: number;
}

interface ImpactComparison {
  before: {
    avgTraffic: number;
    avgOrganicPct: number;
    avgSearchPct: number;
    avgAiPct: number;
    days: number;
  };
  after: {
    avgTraffic: number;
    avgOrganicPct: number;
    avgSearchPct: number;
    avgAiPct: number;
    days: number;
  };
  changes: {
    trafficChange: number;
    organicPctChange: number;
    aiPctChange: number;
    searchPctChange: number;
  };
}

interface ImpactData {
  productInfo: {
    aeoId: string;
    productName: string;
    pageUrl: string;
    approvedAt: string;
    verifiedAt: string | null;
  } | null;
  productCount: number;
  hasUrlLevelData: boolean;
  dateRange: {
    start: string;
    end: string;
  };
  markers: {
    approvalDate: string;
    verificationDate: string | null;
    firstAIVisit: string | null;
  };
  timeline: Array<{
    date: string;
    totalVisits: number;
    googlebotVisits: number;
    bingbotVisits: number;
    aiVisits: number;
    urlOrganic?: {
      google: number;
      bing: number;
      chatgpt: number;
      perplexity: number;
      claude: number;
      direct: number;
      referral: number;
      searchTotal: number;
      aiTotal: number;
    };
  }>;
  organicBreakdown: ImpactOrganicData[];
  comparison: ImpactComparison;
}

// Exposure types
interface OtherLanguageVersion {
  url_path: string;
  in_sitemap: boolean;
  position: number | null;
  last_bot_visit: string | null;
  bot_visits_30d: number;
}

interface ExposureSummary {
  totalPublished: number;
  totalImproved: number;
  withBotVisitsAfterApproval: number;
  productsWithAIBotVisits: number;
  productsWithSearchBotVisits: number;
  totalHumanTraffic: number;
  totalAIBotVisits: number;
  totalSearchBotVisits: number;
  avgPerformanceScore: number;
  topPerformersCount: number;
  topPerformers: Array<{
    aeoId: string;
    productName: string;
    performanceScore: number;
    totalBotVisits: number;
    totalHumanVisits: number;
    hasAIBotVisits: boolean;
    hasSearchBotVisits: boolean;
  }>;
  daysWindow: number;
}

interface ExposureItem {
  aeoId: string;
  productName: string;
  pageUrl: string;
  urlPath: string;
  productId: string;
  approvedAt: string;
  deploymentStatus: string | null;
  verifiedAt: string | null;
  // Performance metrics
  performanceScore: number;
  hasAIBotVisits: boolean;
  hasSearchBotVisits: boolean;
  aiBotVisits: number;
  searchBotVisits: number;
  // Exact URL stats
  inSitemap: boolean;
  sitemapPosition: number | null;
  lastBotVisit: string | null;
  botVisits30d: number;
  // Aggregated across all language variants
  variantsInSitemap: number;
  totalVariants: number;
  bestSitemapPosition: number | null;
  lastBotVisitAny: string | null;
  totalBotVisits30d: number;
  // Bot stats from crawl_url_metrics (source of truth, not sitemap-dependent)
  totalBotVisits30dSitemap?: number;
  urlsWithBotVisits?: number;
  totalBotVisits30dActual?: number;
  lastBotVisitActual?: string | null;
  totalHumanVisits: number;
  lastTrafficDate: string | null;
  // All language versions
  allLanguageVersions: OtherLanguageVersion[];
  // Direct traffic metrics for exact path
  botVisits: Record<string, number> | null;
  humanVisits: number | null;
  lastMetricsDate: string | null;
}

// Helper functions
function getFaqStatusColor(status: string): 'default' | 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'deployed':
    case 'publisher_confirmed':
      return 'default';
    case 'partial':
    case 'modified':
      return 'warning';
    case 'reported':
      return 'secondary';
    case 'not_found':
    case 'page_error':
    case 'js_required':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function getFaqStatusLabel(status: string): string {
  switch (status) {
    case 'deployed':
      return 'Deployed';
    case 'publisher_confirmed':
      return 'Publisher Confirmed';
    case 'partial':
      return 'Partial';
    case 'modified':
      return 'Modified';
    case 'reported':
      return 'Reported';
    case 'not_found':
      return 'Not Found';
    case 'page_error':
      return 'Page Error';
    case 'js_required':
      return 'JS Required';
    default:
      return status;
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString();
}

function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
}

export default function DeploymentVerificationDashboard() {
  const { selectedSite } = useSite();

  // Stats state
  const [stats, setStats] = useState<VerificationStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // History state
  const [history, setHistory] = useState<VerificationItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Batch verification state
  const [runningBatch, setRunningBatch] = useState(false);
  const [ratePreset, setRatePreset] = useState<'gentle' | 'normal' | 'fast'>('normal');
  const [reportedOnly, setReportedOnly] = useState(false);
  const [verificationLimit, setVerificationLimit] = useState(50);

  // Product search state
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductWithAEO[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [verifyingSelected, setVerifyingSelected] = useState(false);
  const [verificationResults, setVerificationResults] = useState<VerificationResult[]>([]);
  const [categories, setCategories] = useState<{ category: string; productCount: number }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [deploymentStatusFilter, setDeploymentStatusFilter] = useState<string>('all');

  // Impact tab state
  const [impactProducts, setImpactProducts] = useState<ImpactProduct[]>([]);
  const [impactProductsLoading, setImpactProductsLoading] = useState(false);
  const [selectedImpactProduct, setSelectedImpactProduct] = useState<string>('all');
  const [impactData, setImpactData] = useState<ImpactData | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactDaysBefore, setImpactDaysBefore] = useState(30);
  const [impactDaysAfter, setImpactDaysAfter] = useState(30);

  // Exposure tab state
  const [exposureData, setExposureData] = useState<ExposureItem[]>([]);
  const [exposureSummary, setExposureSummary] = useState<ExposureSummary | null>(null);
  const [exposureLoading, setExposureLoading] = useState(false);
  const [exposureDaysWindow, setExposureDaysWindow] = useState<number>(30);
  const [exposureSortBy, setExposureSortBy] = useState<string>('approvedAt');
  const [exposureSortOrder, setExposureSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Verification method config state
  const [verificationMethod, setVerificationMethod] = useState<string>('manual');

  // Load stats
  const loadStats = useCallback(async () => {
    if (!selectedSite?.id) {
      setStats(null);
      setLoadingStats(false);
      return;
    }

    setLoadingStats(true);
    try {
      const response = await authFetch(`/content-review/verification-stats/${selectedSite.id}`);
      if (!response.ok) throw new Error('Failed to load stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load verification stats',
        variant: 'destructive',
      });
    } finally {
      setLoadingStats(false);
    }
  }, [selectedSite?.id]);

  // Load history
  const loadHistory = useCallback(async () => {
    if (!selectedSite?.id) {
      setHistory([]);
      setLoadingHistory(false);
      return;
    }

    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      params.set('siteId', selectedSite.id);
      params.set('page', page.toString());
      params.set('limit', '20');
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }

      const response = await authFetch(`/content-review/verification-history?${params}`);
      if (!response.ok) throw new Error('Failed to load history');
      const data: VerificationHistoryResponse = await response.json();
      if (data.success) {
        setHistory(data.data.items);
        setTotalPages(data.data.totalPages);
        setTotal(data.data.total);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast({
        title: 'Error',
        description: 'Failed to load verification history',
        variant: 'destructive',
      });
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedSite?.id, page, statusFilter]);

  // Run batch verification
  const runBatchVerification = async () => {
    if (!selectedSite?.id) return;

    setRunningBatch(true);
    try {
      const response = await authFetch('/content-review/verify-deployments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          limit: verificationLimit,
          ratePreset: ratePreset,
          reportedOnly: reportedOnly,
          httpOnly: true,
        }),
      });
      if (!response.ok) throw new Error('Failed to run verification');
      const data = await response.json();

      if (data.success) {
        const result = data.data;
        let description = `Verified ${result.verified} products: ${result.deployed} deployed, ${result.partial} partial, ${result.notFound} not found`;
        if (result.errors > 0) description += `, ${result.errors} errors`;
        if (result.circuitBroken) description += ' (stopped: high error rate)';
        if (result.cancelled) description += ' (cancelled)';

        toast({
          title: 'Batch Verification Complete',
          description,
        });
        // Reload data
        loadStats();
        loadHistory();
      }
    } catch (error) {
      console.error('Error running batch verification:', error);
      toast({
        title: 'Error',
        description: 'Failed to run batch verification',
        variant: 'destructive',
      });
    } finally {
      setRunningBatch(false);
    }
  };

  // Load categories for product search
  const loadCategories = useCallback(async () => {
    if (!selectedSite?.id) return;

    try {
      const response = await authFetch(`/content-review/product-categories-with-aeo/${selectedSite.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCategories(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [selectedSite?.id]);

  // Load published products for impact analysis
  const loadImpactProducts = useCallback(async () => {
    if (!selectedSite?.id) return;

    setImpactProductsLoading(true);
    try {
      const response = await authFetch(`/content-review/impact/${selectedSite.id}/products`);
      if (!response.ok) throw new Error('Failed to load products');

      const data = await response.json();
      if (data.success) {
        setImpactProducts(data.data);
      }
    } catch (error) {
      console.error('Error loading impact products:', error);
    } finally {
      setImpactProductsLoading(false);
    }
  }, [selectedSite?.id]);

  // Load impact data for selected product or aggregate
  const loadImpactData = useCallback(async () => {
    if (!selectedSite?.id) return;

    setImpactLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedImpactProduct !== 'all') {
        params.set('aeoId', selectedImpactProduct);
      }
      params.set('daysBefore', impactDaysBefore.toString());
      params.set('daysAfter', impactDaysAfter.toString());

      const response = await authFetch(`/content-review/impact/${selectedSite.id}?${params}`);
      if (!response.ok) throw new Error('Failed to load impact data');

      const data = await response.json();
      if (data.success) {
        setImpactData(data.data);
      } else {
        setImpactData(null);
        toast({
          title: 'No Data Available',
          description: data.error || 'No published products with approval dates found',
        });
      }
    } catch (error) {
      console.error('Error loading impact data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load impact data',
        variant: 'destructive',
      });
    } finally {
      setImpactLoading(false);
    }
  }, [selectedSite?.id, selectedImpactProduct, impactDaysBefore, impactDaysAfter]);

  // Load exposure data for published products
  const loadExposureData = useCallback(async () => {
    if (!selectedSite?.id) return;

    setExposureLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('daysWindow', exposureDaysWindow.toString());
      params.set('sortBy', exposureSortBy);
      params.set('sortOrder', exposureSortOrder);

      const response = await authFetch(`/content-review/exposure/${selectedSite.id}?${params}`);
      if (!response.ok) throw new Error('Failed to load exposure data');

      const data = await response.json();
      if (data.success) {
        setExposureData(data.data);
        setExposureSummary(data.summary);
      }
    } catch (error) {
      console.error('Error loading exposure data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load exposure data',
        variant: 'destructive',
      });
    } finally {
      setExposureLoading(false);
    }
  }, [selectedSite?.id, exposureDaysWindow, exposureSortBy, exposureSortOrder]);

  // Toggle sort for exposure table
  const toggleExposureSort = (field: string) => {
    if (exposureSortBy === field) {
      setExposureSortOrder(exposureSortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setExposureSortBy(field);
      setExposureSortOrder('DESC');
    }
  };

  // Search products with AEO
  const searchProducts = useCallback(async (resetPage = false) => {
    if (!selectedSite?.id) return;

    const currentPage = resetPage ? 1 : searchPage;
    if (resetPage) setSearchPage(1);

    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('siteId', selectedSite.id);
      params.set('page', currentPage.toString());
      params.set('limit', '20');

      if (searchQuery) {
        params.set('search', searchQuery);
      }
      if (selectedCategory !== 'all') {
        params.set('category', selectedCategory);
      }
      if (deploymentStatusFilter !== 'all') {
        params.set('status', deploymentStatusFilter);
      }

      const response = await authFetch(`/content-review/products-with-aeo?${params}`);
      if (!response.ok) throw new Error('Failed to search products');

      const data = await response.json();
      if (data.success) {
        setSearchResults(data.data.items);
        setSearchTotal(data.data.total);
        setSearchTotalPages(data.data.totalPages);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      toast({
        title: 'Error',
        description: 'Failed to search products',
        variant: 'destructive',
      });
    } finally {
      setSearchLoading(false);
    }
  }, [selectedSite?.id, searchQuery, selectedCategory, deploymentStatusFilter, searchPage]);

  // Verify selected products
  const verifySelectedProducts = async () => {
    if (!selectedSite?.id || selectedProducts.size === 0) return;

    setVerifyingSelected(true);
    setVerificationResults([]);

    try {
      const response = await authFetch('/content-review/verify-selected', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          aeoIds: Array.from(selectedProducts),
          ratePreset: ratePreset,
        }),
      });

      if (!response.ok) throw new Error('Failed to verify products');

      const data = await response.json();

      if (data.success) {
        setVerificationResults(data.data.details);

        const result = data.data;
        toast({
          title: 'Verification Complete',
          description: `Verified ${result.verified} products: ${result.deployed} deployed, ${result.partial} partial, ${result.notFound} not found, ${result.errors} errors`,
        });

        // Clear selection and refresh
        setSelectedProducts(new Set());
        loadStats();
        searchProducts();
      }
    } catch (error) {
      console.error('Error verifying selected products:', error);
      toast({
        title: 'Error',
        description: 'Failed to verify selected products',
        variant: 'destructive',
      });
    } finally {
      setVerifyingSelected(false);
    }
  };

  // Toggle product selection
  const toggleProductSelection = (aeoId: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(aeoId)) {
        newSet.delete(aeoId);
      } else {
        newSet.add(aeoId);
      }
      return newSet;
    });
  };

  // Select all visible products
  const selectAllVisible = () => {
    const allIds = searchResults.map(p => p.aeoId);
    setSelectedProducts(new Set(allIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProducts(new Set());
  };

  // Load data on mount and when site changes
  useEffect(() => {
    loadStats();
    loadHistory();
    loadCategories();
  }, [loadStats, loadHistory, loadCategories]);

  // Search when tab changes to manual
  useEffect(() => {
    if (activeTab === 'manual' && selectedSite?.id) {
      searchProducts(true);
    }
  }, [activeTab, selectedSite?.id]);

  // Search when filters change
  useEffect(() => {
    if (activeTab === 'manual') {
      searchProducts(true);
    }
  }, [selectedCategory, deploymentStatusFilter]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Load impact products when tab changes to impact
  useEffect(() => {
    if (activeTab === 'impact' && selectedSite?.id) {
      loadImpactProducts();
    }
  }, [activeTab, selectedSite?.id, loadImpactProducts]);

  // Load impact data when tab is impact and filters change (or on initial tab view)
  // Consolidated into single useEffect to prevent duplicate queries (was firing 3x per page load)
  // See: https://github.com/pagemind-ai/pagemind-ai-core/issues/374
  useEffect(() => {
    if (activeTab === 'impact' && selectedSite?.id) {
      loadImpactData();
    }
  }, [activeTab, selectedSite?.id, selectedImpactProduct, impactDaysBefore, impactDaysAfter, loadImpactData]);

  // Load exposure data when tab changes to exposure or when params change while on exposure tab
  useEffect(() => {
    if (activeTab === 'exposure' && selectedSite?.id) {
      loadExposureData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSite?.id, exposureDaysWindow, exposureSortBy, exposureSortOrder]);

  // Update active tab when verification method changes
  useEffect(() => {
    // Set default tab based on method
    if (verificationMethod === 'manual') {
      setActiveTab('overview');
    } else if (verificationMethod === 'publisher_manifest') {
      setActiveTab('publisher-manifest');
    } else if (verificationMethod === 'webhook') {
      setActiveTab('webhook-logs');
    } else {
      setActiveTab('impact');
    }
  }, [verificationMethod]);

  if (!selectedSite) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-10">
            <p className="text-center text-muted-foreground">
              Please select a site to view deployment verification status.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Deployment Verification</h1>
          <p className="text-muted-foreground">
            Track if approved AEO FAQs have been deployed to live customer pages
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { loadStats(); loadHistory(); }}
            disabled={loadingStats || loadingHistory}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingStats || loadingHistory ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Verification Method Configuration */}
      <DeploymentVerificationConfig
        siteId={selectedSite.id}
        onMethodChange={setVerificationMethod}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {verificationMethod === 'manual' && (
            <>
              <TabsTrigger value="overview">Overview & Batch</TabsTrigger>
              <TabsTrigger value="manual">Manual Verification</TabsTrigger>
            </>
          )}
          {verificationMethod === 'publisher_manifest' && (
            <TabsTrigger value="publisher-manifest">Publisher Manifest</TabsTrigger>
          )}
          {verificationMethod === 'webhook' && (
            <TabsTrigger value="webhook-logs">Webhook Logs</TabsTrigger>
          )}
          <TabsTrigger value="impact">Traffic Impact</TabsTrigger>
          <TabsTrigger value="exposure">Exposure</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">

      {/* Verification Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Run Manual Verification</CardTitle>
          <CardDescription>
            Verify deployment status for products. Use rate limiting to avoid triggering DOS protection.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Products to verify</label>
              <Select
                value={verificationLimit.toString()}
                onValueChange={(v) => setVerificationLimit(parseInt(v))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Rate</label>
              <Select
                value={ratePreset}
                onValueChange={(v) => setRatePreset(v as 'gentle' | 'normal' | 'fast')}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gentle">Gentle (~720/hr)</SelectItem>
                  <SelectItem value="normal">Normal (~5,400/hr)</SelectItem>
                  <SelectItem value="fast">Fast (~36,000/hr)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Scope</label>
              <Select
                value={reportedOnly ? 'reported' : 'all'}
                onValueChange={(v) => setReportedOnly(v === 'reported')}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pending</SelectItem>
                  <SelectItem value="reported">Reported only ({stats?.reported || 0})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={runBatchVerification}
              disabled={runningBatch || (!reportedOnly && !stats?.pendingVerification) || (reportedOnly && !stats?.reported)}
            >
              {runningBatch ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {runningBatch ? 'Running...' : 'Start Verification'}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            {reportedOnly
              ? `Will verify up to ${verificationLimit} products that customers reported as deployed.`
              : `Will verify up to ${verificationLimit} products from ${stats?.pendingVerification || 0} pending.`}
            {' '}Rate: {ratePreset === 'gentle' ? '~720' : ratePreset === 'normal' ? '~5,400' : '~36,000'} products/hour.
          </p>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Deployed</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-2xl font-bold">{stats?.deployed || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Partial</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <span className="text-2xl font-bold">{stats?.partial || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Modified</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <span className="text-2xl font-bold">{stats?.modified || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Not Found</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                <span className="text-2xl font-bold">{stats?.notFound || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Reported</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-cyan-500" />
                <span className="text-2xl font-bold">{stats?.reported || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Publisher Confirmed</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-emerald-500" />
                <span className="text-2xl font-bold">{stats?.publisherConfirmed || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">{stats?.pendingVerification || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg. Similarity</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-purple-500" />
                <span className="text-2xl font-bold">
                  {stats?.avgSimilarity ? `${Math.round(stats.avgSimilarity * 100)}%` : 'N/A'}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Last Verified */}
      {stats?.lastVerified && (
        <p className="text-sm text-muted-foreground">
          Last verification: {formatDate(stats.lastVerified)}
        </p>
      )}

      {/* History Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Verification History</CardTitle>
              <CardDescription>
                {total} total verifications
              </CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="publisher_confirmed">Publisher Confirmed</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="modified">Modified</SelectItem>
                <SelectItem value="not_found">Not Found</SelectItem>
                <SelectItem value="page_error">Page Error</SelectItem>
                <SelectItem value="js_required">JS Required</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No verification records yet. Click "Run Verification" to start.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Similarity</TableHead>
                    <TableHead>FAQs</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs max-w-[300px]">
                        <span title={item.url}>{truncateUrl(item.url)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getFaqStatusColor(item.faqStatus)}>
                          {getFaqStatusLabel(item.faqStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {Math.round(item.faqSimilarity * 100)}%
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.details?.matchedCount !== undefined ? (
                          <span>
                            {item.details.matchedCount}/{item.details.ourCount} matched
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {item.details?.ourCount || 0} ours / {item.details?.liveCount || 0} live
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(item.lastVerified)}
                      </TableCell>
                      <TableCell>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        {/* Manual Verification Tab */}
        <TabsContent value="manual" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Search & Verify Products</CardTitle>
              <CardDescription>
                Search for products with AEO content, select specific ones, and verify their deployment in real-time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by product name, SKU, or URL..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchProducts(true)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.category} value={cat.category}>
                        {cat.category} ({cat.productCount})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={deploymentStatusFilter} onValueChange={setDeploymentStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Deployment Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="deployed">Deployed</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="modified">Modified</SelectItem>
                    <SelectItem value="not_found">Not Found</SelectItem>
                    <SelectItem value="reported">Reported</SelectItem>
                  </SelectContent>
                </Select>

                <Button onClick={() => searchProducts(true)} disabled={searchLoading}>
                  {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  <span className="ml-2">Search</span>
                </Button>
              </div>

              {/* Selection Controls */}
              {searchResults.length > 0 && (
                <div className="flex items-center justify-between border-b pb-3">
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={selectAllVisible}>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearSelection} disabled={selectedProducts.size === 0}>
                      Clear Selection
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {selectedProducts.size} selected of {searchTotal} products
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select
                      value={ratePreset}
                      onValueChange={(v) => setRatePreset(v as 'gentle' | 'normal' | 'fast')}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gentle">Gentle</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="fast">Fast</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      onClick={verifySelectedProducts}
                      disabled={selectedProducts.size === 0 || verifyingSelected}
                    >
                      {verifyingSelected ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Verify Selected ({selectedProducts.size})
                    </Button>
                  </div>
                </div>
              )}

              {/* Results Table */}
              {searchLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No products found. Try adjusting your search or filters.
                </p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>FAQs</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Similarity</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchResults.map((product) => {
                        // Check if this product was just verified
                        const verifyResult = verificationResults.find(r => r.aeoId === product.aeoId);

                        return (
                          <TableRow
                            key={product.aeoId}
                            className={verifyResult ? 'bg-green-50 dark:bg-green-950/20' : ''}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedProducts.has(product.aeoId)}
                                onCheckedChange={() => toggleProductSelection(product.aeoId)}
                              />
                            </TableCell>
                            <TableCell className="max-w-[250px]">
                              <div className="truncate font-medium" title={product.productTitle}>
                                {product.productTitle}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {product.sku}
                            </TableCell>
                            <TableCell className="text-sm">
                              {product.category || '-'}
                            </TableCell>
                            <TableCell>
                              {product.faqCount}
                            </TableCell>
                            <TableCell>
                              {verifyResult ? (
                                <Badge variant={getFaqStatusColor(verifyResult.faqStatus)}>
                                  {getFaqStatusLabel(verifyResult.faqStatus)}
                                </Badge>
                              ) : product.deploymentStatus ? (
                                <Badge variant={getFaqStatusColor(product.deploymentStatus)}>
                                  {getFaqStatusLabel(product.deploymentStatus)}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">Not verified</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {verifyResult?.similarity !== undefined
                                ? `${Math.round(verifyResult.similarity * 100)}%`
                                : product.similarity !== null
                                  ? `${Math.round(product.similarity * 100)}%`
                                  : '-'}
                            </TableCell>
                            <TableCell>
                              <a
                                href={product.productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {searchTotalPages > 1 && (
                    <div className="flex justify-between items-center mt-4">
                      <p className="text-sm text-muted-foreground">
                        Page {searchPage} of {searchTotalPages} ({searchTotal} products)
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSearchPage(p => Math.max(1, p - 1));
                            searchProducts();
                          }}
                          disabled={searchPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSearchPage(p => Math.min(searchTotalPages, p + 1));
                            searchProducts();
                          }}
                          disabled={searchPage === searchTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Publisher Manifest Tab */}
        <TabsContent value="publisher-manifest" className="space-y-6">
          <PublisherManifestSection siteId={selectedSite.id} />
        </TabsContent>

        {/* Webhook Logs Tab */}
        <TabsContent value="webhook-logs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Deployment Logs</CardTitle>
              <CardDescription>
                Real-time logs of URLs received via webhook from your deployment system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground py-8">
                Configure the webhook in the settings above, then your system can push deployed URLs here.
                Webhook logs will appear in this section.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Impact Tab */}
        <TabsContent value="impact" className="space-y-6">
          {/* Controls */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Traffic Impact Analysis</CardTitle>
              <CardDescription>
                Compare traffic patterns before and after AEO content was deployed to measure impact.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Product</label>
                  <Select
                    value={selectedImpactProduct}
                    onValueChange={setSelectedImpactProduct}
                    disabled={impactProductsLoading}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select a product..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Published Products ({impactProducts.length})</SelectItem>
                      {impactProducts.map((p) => (
                        <SelectItem key={p.aeoId} value={p.aeoId}>
                          {p.productName.substring(0, 50)}{p.productName.length > 50 ? '...' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Days Before Approval</label>
                  <Select
                    value={impactDaysBefore.toString()}
                    onValueChange={(v) => setImpactDaysBefore(parseInt(v))}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Days After Approval</label>
                  <Select
                    value={impactDaysAfter.toString()}
                    onValueChange={(v) => setImpactDaysAfter(parseInt(v))}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  onClick={loadImpactData}
                  disabled={impactLoading}
                >
                  {impactLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </div>

              {impactData?.productInfo && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm font-medium">{impactData.productInfo.productName}</span>
                      <div className="text-xs text-muted-foreground">
                        Approved: {formatDate(impactData.productInfo.approvedAt)}
                        {impactData.productInfo.verifiedAt && (
                          <span> | Verified: {formatDate(impactData.productInfo.verifiedAt)}</span>
                        )}
                      </div>
                    </div>
                    <a
                      href={impactData.productInfo.pageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              )}

              {!impactData?.hasUrlLevelData && impactData && (
                <div className="mt-4 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-md">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  URL-level traffic data not available. Showing site-level organic traffic breakdown.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Before/After Comparison Cards */}
          {impactLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : impactData ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Traffic Change</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {impactData.comparison.changes.trafficChange >= 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`text-2xl font-bold ${impactData.comparison.changes.trafficChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impactData.comparison.changes.trafficChange >= 0 ? '+' : ''}{impactData.comparison.changes.trafficChange}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {impactData.comparison.before.avgTraffic} → {impactData.comparison.after.avgTraffic} avg/day
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Organic % Change</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {impactData.comparison.changes.organicPctChange >= 0 ? (
                      <ArrowUp className="h-5 w-5 text-green-500" />
                    ) : (
                      <ArrowDown className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`text-2xl font-bold ${impactData.comparison.changes.organicPctChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impactData.comparison.changes.organicPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.organicPctChange}pp
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {impactData.comparison.before.avgOrganicPct}% → {impactData.comparison.after.avgOrganicPct}% of traffic
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Search Organic Change</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {impactData.comparison.changes.searchPctChange >= 0 ? (
                      <ArrowUp className="h-5 w-5 text-green-500" />
                    ) : (
                      <ArrowDown className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`text-2xl font-bold ${impactData.comparison.changes.searchPctChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impactData.comparison.changes.searchPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.searchPctChange}pp
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Google/Bing organic
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>AI Organic Change</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {impactData.comparison.changes.aiPctChange >= 0 ? (
                      <ArrowUp className="h-5 w-5 text-green-500" />
                    ) : (
                      <ArrowDown className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`text-2xl font-bold ${impactData.comparison.changes.aiPctChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impactData.comparison.changes.aiPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.aiPctChange}pp
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    ChatGPT/Claude/Perplexity referrals
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : null}

          {/* URL-Level AI Traffic Breakdown */}
          {impactData?.hasUrlLevelData && impactData.timeline.some(t => t.urlOrganic && (t.urlOrganic.aiTotal > 0 || t.urlOrganic.searchTotal > 0)) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  URL-Level Traffic Attribution
                </CardTitle>
                <CardDescription>
                  Direct AI surface and search traffic to deployed product pages (not site-wide estimates).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {(() => {
                    // Aggregate URL-level organic sources across all days
                    const totals = impactData.timeline.reduce((acc, t) => {
                      if (t.urlOrganic) {
                        acc.chatgpt += t.urlOrganic.chatgpt;
                        acc.perplexity += t.urlOrganic.perplexity;
                        acc.claude += t.urlOrganic.claude;
                        acc.google += t.urlOrganic.google;
                        acc.bing += t.urlOrganic.bing;
                        acc.direct += t.urlOrganic.direct;
                        acc.referral += t.urlOrganic.referral;
                      }
                      return acc;
                    }, { chatgpt: 0, perplexity: 0, claude: 0, google: 0, bing: 0, direct: 0, referral: 0 });

                    return (
                      <>
                        <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">ChatGPT Referrals</div>
                          <div className="text-xl font-bold text-purple-600">{totals.chatgpt.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Perplexity Referrals</div>
                          <div className="text-xl font-bold text-orange-600">{totals.perplexity.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Claude Referrals</div>
                          <div className="text-xl font-bold text-amber-600">{totals.claude.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Google Organic</div>
                          <div className="text-xl font-bold text-blue-600">{totals.google.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-cyan-50 dark:bg-cyan-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Bing Organic</div>
                          <div className="text-xl font-bold text-cyan-600">{totals.bing.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Direct</div>
                          <div className="text-xl font-bold text-gray-600">{totals.direct.toLocaleString()}</div>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-950/20 rounded-md">
                          <div className="text-xs text-muted-foreground">Referral</div>
                          <div className="text-xl font-bold text-slate-600">{totals.referral.toLocaleString()}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Traffic Chart */}
          {impactLoading ? (
            <Skeleton className="h-[400px] w-full" />
          ) : impactData && impactData.organicBreakdown.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Traffic Over Time</CardTitle>
                <CardDescription>
                  Daily organic traffic breakdown from {impactData.dateRange.start} to {impactData.dateRange.end}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={impactData.organicBreakdown.map(d => ({
                        ...d,
                        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        label={{ value: 'Traffic', angle: -90, position: 'insideLeft' }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, 100]}
                        label={{ value: '% of Traffic', angle: 90, position: 'insideRight' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />

                      {/* Reference line for approval date */}
                      {impactData.markers.approvalDate && (
                        <ReferenceLine
                          x={new Date(impactData.markers.approvalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          yAxisId="left"
                          stroke="#22c55e"
                          strokeDasharray="5 5"
                          label={{ value: 'Approved', position: 'top', fill: '#22c55e', fontSize: 10 }}
                        />
                      )}

                      {/* Reference line for first AI visit */}
                      {impactData.markers.firstAIVisit && (
                        <ReferenceLine
                          x={new Date(impactData.markers.firstAIVisit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          yAxisId="left"
                          stroke="#8b5cf6"
                          strokeDasharray="5 5"
                          label={{ value: '1st AI Bot', position: 'top', fill: '#8b5cf6', fontSize: 10 }}
                        />
                      )}

                      {/* Reference line for verification date */}
                      {impactData.markers.verificationDate && (
                        <ReferenceLine
                          x={new Date(impactData.markers.verificationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          yAxisId="left"
                          stroke="#3b82f6"
                          strokeDasharray="5 5"
                          label={{ value: 'Verified', position: 'top', fill: '#3b82f6', fontSize: 10 }}
                        />
                      )}

                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="totalTraffic"
                        name="Total Traffic"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="organicPct"
                        name="Organic %"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="searchPct"
                        name="Search Organic %"
                        stroke="#3b82f6"
                        strokeWidth={1}
                        dot={false}
                        strokeDasharray="3 3"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="aiPct"
                        name="AI Organic %"
                        stroke="#8b5cf6"
                        strokeWidth={1}
                        dot={false}
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : !impactLoading && (
            <Card>
              <CardContent className="py-10">
                <p className="text-center text-muted-foreground">
                  No traffic data available for the selected date range.
                  {impactProducts.length === 0 && (
                    <span className="block mt-2">
                      No published products with approval dates found.
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Period Comparison Table */}
          {impactData && (
            <Card>
              <CardHeader>
                <CardTitle>Period Comparison</CardTitle>
                <CardDescription>
                  Average daily metrics before and after AEO content approval
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead className="text-right">Before ({impactData.comparison.before.days} days)</TableHead>
                      <TableHead className="text-right">After ({impactData.comparison.after.days} days)</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Avg. Daily Traffic</TableCell>
                      <TableCell className="text-right">{impactData.comparison.before.avgTraffic.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{impactData.comparison.after.avgTraffic.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <span className={impactData.comparison.changes.trafficChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {impactData.comparison.changes.trafficChange >= 0 ? '+' : ''}{impactData.comparison.changes.trafficChange}%
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Organic Traffic %</TableCell>
                      <TableCell className="text-right">{impactData.comparison.before.avgOrganicPct}%</TableCell>
                      <TableCell className="text-right">{impactData.comparison.after.avgOrganicPct}%</TableCell>
                      <TableCell className="text-right">
                        <span className={impactData.comparison.changes.organicPctChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {impactData.comparison.changes.organicPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.organicPctChange}pp
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Search Organic %</TableCell>
                      <TableCell className="text-right">{impactData.comparison.before.avgSearchPct}%</TableCell>
                      <TableCell className="text-right">{impactData.comparison.after.avgSearchPct}%</TableCell>
                      <TableCell className="text-right">
                        <span className={impactData.comparison.changes.searchPctChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {impactData.comparison.changes.searchPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.searchPctChange}pp
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">AI Organic %</TableCell>
                      <TableCell className="text-right">{impactData.comparison.before.avgAiPct}%</TableCell>
                      <TableCell className="text-right">{impactData.comparison.after.avgAiPct}%</TableCell>
                      <TableCell className="text-right">
                        <span className={impactData.comparison.changes.aiPctChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {impactData.comparison.changes.aiPctChange >= 0 ? '+' : ''}{impactData.comparison.changes.aiPctChange}pp
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Exposure Tab */}
        <TabsContent value="exposure" className="space-y-6">
          {/* Controls Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle>AEO Product Exposure</CardTitle>
                  <CardDescription>
                    Sitemap presence, bot crawling status, and traffic for published AEO products.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Time window:</span>
                    <Select
                      value={exposureDaysWindow.toString()}
                      onValueChange={(v) => setExposureDaysWindow(parseInt(v))}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    onClick={loadExposureData}
                    disabled={exposureLoading}
                  >
                    {exposureLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {exposureLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : exposureData.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  No published products found.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary KPIs Card - Answers the 3 Key Questions */}
              {exposureSummary && (
                <Card className="border-2 border-primary/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-500" />
                      Impact Summary (Last {exposureSummary.daysWindow} Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Question 1: How many products are published and improved? */}
                      <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 mb-2">
                          <FileCheck className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800 dark:text-green-200">Published & Improved</span>
                        </div>
                        <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                          {exposureSummary.totalImproved.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          of {exposureSummary.totalPublished.toLocaleString()} total published
                        </div>
                      </div>

                      {/* Question 2: How many got visited by bots after improvement? */}
                      <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="h-5 w-5 text-purple-600" />
                          <span className="text-sm font-medium text-purple-800 dark:text-purple-200">Bot Crawled After Approval</span>
                        </div>
                        <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                          {exposureSummary.withBotVisitsAfterApproval.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {exposureSummary.productsWithAIBotVisits} AI / {exposureSummary.productsWithSearchBotVisits} search
                        </div>
                      </div>

                      {/* Question 3: How much traffic have they gotten? */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Human Traffic</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                          {exposureSummary.totalHumanTraffic.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          visits across all products
                        </div>
                      </div>
                    </div>

                    {/* Additional stats row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
                      <div className="text-center">
                        <div className="text-lg font-semibold">{exposureSummary.avgPerformanceScore}</div>
                        <div className="text-xs text-muted-foreground">Avg Score</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-amber-600">{exposureSummary.topPerformersCount}</div>
                        <div className="text-xs text-muted-foreground">Top Performers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-purple-600">{exposureSummary.totalAIBotVisits.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">AI Bot Visits</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-blue-600">{exposureSummary.totalSearchBotVisits.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">Search Bot Visits</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold">{exposureData.filter(e => e.variantsInSitemap > 0).length}</div>
                        <div className="text-xs text-muted-foreground">In Sitemap</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Performers Section */}
              {exposureSummary && exposureSummary.topPerformers.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-500" />
                      Top Performers ({exposureSummary.topPerformers.length} products, score &ge; 70)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {exposureSummary.topPerformers.map((product, index) => (
                        <div
                          key={product.aeoId}
                          className="p-3 rounded-lg border bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-amber-600">#{index + 1}</span>
                            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                              {product.performanceScore}
                            </Badge>
                          </div>
                          <div className="font-medium text-sm truncate" title={product.productName}>
                            {product.productName}
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Bot className="h-3 w-3" />
                              {product.totalBotVisits}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {product.totalHumanVisits.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data table */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">All Products</CardTitle>
                  <CardDescription>
                    Click column headers to sort. Shows aggregated data across all language variants.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleExposureSort('performanceScore')}
                        >
                          <div className="flex items-center gap-1">
                            Score
                            {exposureSortBy === 'performanceScore' && (
                              exposureSortOrder === 'DESC' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                            )}
                            {exposureSortBy !== 'performanceScore' && <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        </TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center">Sitemap</TableHead>
                        <TableHead
                          className="text-center cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleExposureSort('lastBotVisit')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Last Bot
                            {exposureSortBy === 'lastBotVisit' && (
                              exposureSortOrder === 'DESC' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                            )}
                            {exposureSortBy !== 'lastBotVisit' && <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        </TableHead>
                        <TableHead
                          className="text-center cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleExposureSort('botVisits')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Bot Visits
                            {exposureSortBy === 'botVisits' && (
                              exposureSortOrder === 'DESC' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                            )}
                            {exposureSortBy !== 'botVisits' && <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        </TableHead>
                        <TableHead
                          className="text-center cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleExposureSort('humanVisits')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            Human
                            {exposureSortBy === 'humanVisits' && (
                              exposureSortOrder === 'DESC' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                            )}
                            {exposureSortBy !== 'humanVisits' && <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exposureData.map((item) => (
                        <TableRow
                          key={item.aeoId}
                          className={`
                            ${item.variantsInSitemap === 0 ? 'bg-red-50 dark:bg-red-950/20' : ''}
                            ${item.performanceScore >= 70 ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}
                          `}
                        >
                          <TableCell className="text-center">
                            <Badge
                              variant={item.performanceScore >= 70 ? 'default' : item.performanceScore >= 40 ? 'secondary' : 'outline'}
                              className={`
                                ${item.performanceScore >= 70 ? 'bg-amber-500 hover:bg-amber-600' : ''}
                              `}
                            >
                              {item.performanceScore}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[250px]">
                            <div className="truncate font-medium" title={item.productName}>
                              {item.productName}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <span>ID: {item.productId}</span>
                              {item.hasAIBotVisits && <Badge variant="outline" className="text-[10px] px-1 py-0">AI</Badge>}
                              {item.hasSearchBotVisits && <Badge variant="outline" className="text-[10px] px-1 py-0">Search</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {item.variantsInSitemap > 0 ? (
                              <div title={`${item.variantsInSitemap} of ${item.totalVariants} variants in sitemap`}>
                                <span className="font-medium text-green-600">{item.variantsInSitemap}</span>
                                <span className="text-muted-foreground">/{item.totalVariants}</span>
                              </div>
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                            )}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {item.lastBotVisitAny ? (
                              new Date(item.lastBotVisitAny).toLocaleDateString()
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-medium">{(item.totalBotVisits30dActual || item.totalBotVisits30d || 0).toLocaleString()}</span>
                              <div className="flex gap-1 text-[10px]">
                                {item.aiBotVisits > 0 && (
                                  <span className="text-purple-600">{item.aiBotVisits} AI</span>
                                )}
                                {item.searchBotVisits > 0 && (
                                  <span className="text-blue-600">{item.searchBotVisits} S</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {item.totalHumanVisits > 0 ? (
                              <span className="font-medium">{item.totalHumanVisits.toLocaleString()}</span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <a
                              href={item.pageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
